import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Copy, Check, AlertTriangle, Loader2 } from "lucide-react";
import GoogleAnalytics from "@/components/GoogleAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 24; // ~60s

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || localStorage.getItem("fp_last_order_id");
  const [state, setState] = useState({
    phase: orderId ? "polling" : "missing", // polling | success | failed | expired | missing | error
    business_status: null,
    midtrans_status: null,
    premium_key: null,
    expires_at: null,
    error: null,
    attempts: 0,
  });
  const [copied, setCopied] = useState(false);
  const stopped = useRef(false);

  useEffect(() => {
    if (!orderId) return undefined;
    stopped.current = false;
    let attempt = 0;

    const poll = async () => {
      if (stopped.current) return;
      try {
        const res = await axios.get(`${API}/payments/status/${orderId}`, { timeout: 15000 });
        const d = res.data || {};
        const bs = d.business_status;
        if (bs === "succeeded") {
          if (d.premium_key) {
            localStorage.setItem("fp_premium_key", d.premium_key);
          }
          setState((s) => ({
            ...s,
            phase: "success",
            business_status: bs,
            midtrans_status: d.midtrans_transaction_status,
            premium_key: d.premium_key,
            expires_at: d.expires_at,
          }));
          stopped.current = true;
          return;
        }
        if (bs === "failed") {
          setState((s) => ({ ...s, phase: "failed", business_status: bs, midtrans_status: d.midtrans_transaction_status }));
          stopped.current = true;
          return;
        }
        if (bs === "expired") {
          setState((s) => ({ ...s, phase: "expired", business_status: bs }));
          stopped.current = true;
          return;
        }
        attempt += 1;
        setState((s) => ({ ...s, attempts: attempt, business_status: bs, midtrans_status: d.midtrans_transaction_status }));
        if (attempt >= MAX_POLL_ATTEMPTS) {
          setState((s) => ({ ...s, phase: "failed", error: "Timeout — pembayaran belum terkonfirmasi. Cek ulang nanti dengan order ID Anda." }));
          stopped.current = true;
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        attempt += 1;
        setState((s) => ({ ...s, attempts: attempt, error: e?.response?.data?.detail || e.message }));
        if (attempt >= MAX_POLL_ATTEMPTS) {
          setState((s) => ({ ...s, phase: "error" }));
          stopped.current = true;
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => {
      stopped.current = true;
    };
  }, [orderId]);

  const copyKey = async () => {
    if (!state.premium_key) return;
    try {
      await navigator.clipboard.writeText(state.premium_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div className="brutal min-h-screen bg-[#fdfdfd] text-[#0a0a0a]">
      <GoogleAnalytics measurementId="G-L8R7WSYR5P" />
      <header className="border-b-2 border-black bg-[#fdfdfd]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-20 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="success-logo">
            <div className="w-8 h-8 bg-black text-white grid place-items-center font-mono font-bold">FP</div>
            <span className="font-display font-black uppercase tracking-tight text-lg md:text-xl">
              Fakta Produk
            </span>
          </Link>
          <Link
            to="/"
            className="border-2 border-black bg-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-yellow-50"
            data-testid="back-home"
          >
            ← Kembali ke Beranda
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-[900px] px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#666]">
          / Receipt — Premium Access
        </div>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl mt-2 leading-[0.95]" data-testid="success-heading">
          {state.phase === "success" ? "Pembayaran Sukses." : "Memproses Pembayaran..."}
        </h1>

        {!orderId && (
          <div className="mt-8 border-2 border-black bg-[#FF2400] text-white p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em]">[ERROR]</div>
            <div className="font-display font-bold text-2xl mt-1">Order ID tidak ditemukan.</div>
            <p className="mt-2 text-sm">Akses halaman ini hanya melalui redirect dari Midtrans.</p>
          </div>
        )}

        {state.phase === "polling" && orderId && (
          <div className="mt-8 border-2 border-black bg-white p-6 md:p-8 shadow-brutal" data-testid="polling-state">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#666]">[POLLING MIDTRANS]</div>
            <div className="mt-3 flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin" />
              <div className="font-display font-bold text-2xl">
                Verifikasi pembayaran... ({state.attempts}/{MAX_POLL_ATTEMPTS})
              </div>
            </div>
            <p className="mt-3 font-sans text-sm text-[#555]">
              Mohon tunggu, kami sedang mengkonfirmasi pembayaran Anda ke Midtrans. Jangan tutup tab ini.
            </p>
          </div>
        )}

        {state.phase === "success" && (
          <div className="mt-8 border-2 border-black bg-[#002FA7] text-white p-6 md:p-8 shadow-brutal" data-testid="success-state">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-10 h-10" strokeWidth={2.5} />
              <div className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
                Premium Aktif.
              </div>
            </div>
            <p className="mt-3 font-sans text-base">
              Terima kasih. Akses Premium Anda sudah aktif dan otomatis tersimpan di browser ini.
            </p>

            <div className="mt-6 border-2 border-white bg-black p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-70">Kode Akses Premium</div>
              <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="font-mono text-xl md:text-2xl font-bold tracking-wider" data-testid="premium-key-display">
                  {state.premium_key || "—"}
                </div>
                <button
                  type="button"
                  onClick={copyKey}
                  className="bg-white text-black px-3 py-1.5 border-2 border-white font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#FFD700] inline-flex items-center gap-1.5"
                  data-testid="copy-premium-key"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Tersalin" : "Salin"}
                </button>
              </div>
              {state.expires_at && (
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] opacity-80" data-testid="premium-expiry">
                  Berlaku hingga: {new Date(state.expires_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}
                </div>
              )}
            </div>

            <div className="mt-6">
              <Link
                to="/"
                className="inline-block bg-[#FFD700] text-black px-6 py-3 border-2 border-white font-mono font-bold uppercase tracking-[0.18em] hover:bg-white"
                data-testid="go-audit"
              >
                ▶ Mulai Audit Premium
              </Link>
            </div>
          </div>
        )}

        {(state.phase === "failed" || state.phase === "expired" || state.phase === "error") && (
          <div className="mt-8 border-2 border-black bg-[#FF2400] text-white p-6" data-testid="failure-state">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />
              <div className="font-display font-black uppercase tracking-tighter text-2xl">
                {state.phase === "expired" ? "Sesi Pembayaran Kedaluwarsa" : "Pembayaran Belum Terkonfirmasi"}
              </div>
            </div>
            <p className="mt-2 text-sm">{state.error || "Coba lagi atau hubungi support jika dana sudah terpotong."}</p>
            <div className="mt-4">
              <Link
                to="/"
                className="inline-block bg-white text-black px-4 py-2 border-2 border-white font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#FFD700]"
              >
                ← Kembali
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
